// src/App.js
import { useEffect, useState, memo, Component } from "react";
import { Box, ThemeProvider, Typography, useTheme } from "@mui/material";
import theme from "./styles/themes/theme";
import "./App.css";

import { DataProvider, useData } from "./hooks/useData";
import { PricingProvider } from "./hooks/usePricing";

import AppBar from "./components/AppBar";
import HotKeys from "./components/HotKeys";
import CanvasArea from "./components/CanvasArea";
import { getProductByCode } from "./constants/products";

class ErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      console.error("ErrorBoundary caught:", this.state.error);
      return (
        <Typography color="error">
          Something went wrong. Please refresh.
        </Typography>
      );
    }
    return this.props.children;
  }
}

const MainContent = memo(() => {
  const {
    mivaGlobals,
    setMivaGlobals,
    selectedProduct,
    setSelectedProduct,
    setSnackbar,
  } = useData();

  const theme = useTheme();
  const appBarHeight = theme.mixins.toolbar.minHeight || 64;

  const extraHeaderHeight = {
    xs: 100, // mobile: logo + breadcrumb + buttons
    sm: 110,
    md: 120, // desktop
  };

  const totalHeight =
    appBarHeight +
    (extraHeaderHeight.xs +
      (theme.breakpoints.up("sm")
        ? extraHeaderHeight.sm - extraHeaderHeight.xs
        : 0) +
      (theme.breakpoints.up("md")
        ? extraHeaderHeight.md - extraHeaderHeight.sm
        : 0));

  // Or simpler: just use responsive values
  const totalAppBarHeight = {
    xs: 164, // 64 + 100
    sm: 174, // 64 + 110
    md: 184, // 64 + 120
  };

  // ----- MIVA GLOBALS (run once) -----
  useEffect(() => {
    const mivaglobalsDiv = document.getElementById("mivaglobals");
    if (!mivaglobalsDiv) {
      console.warn("MainContent: mivaglobals div not found");
      setSnackbar({
        open: true,
        message: "Miva globals not found, using default product",
        severity: "warning",
      });
      return;
    }

    const mivaGlobals = {
      basketid: mivaglobalsDiv.getAttribute("data-basketid") || "",
      customerid: mivaglobalsDiv.getAttribute("data-customerid") || "",
      csrftokenid: mivaglobalsDiv.getAttribute("data-csrftoken") || "",
      cusessid: mivaglobalsDiv.getAttribute("data-cusessid") || "",
      sessionid: mivaglobalsDiv.getAttribute("data-sessionid") || "",
      productcode: mivaglobalsDiv.getAttribute("data-productcode") || null,
    };

    setMivaGlobals(mivaGlobals);
  }, []);

  // ----- SET PRODUCT ONCE -----
  useEffect(() => {
    if (!mivaGlobals?.productcode || selectedProduct) return;

    const PRODUCT = getProductByCode(mivaGlobals.productcode);
    if (PRODUCT) {
      setSelectedProduct(PRODUCT);
    } else {
      setSnackbar({
        open: true,
        message: `Product ${mivaGlobals.productcode} not found`,
        severity: "error",
      });
    }
  }, [
    mivaGlobals?.productcode,
    selectedProduct,
    setSelectedProduct,
    setSnackbar,
  ]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f3f4f6",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AppBar />
      <HotKeys />
      {/* MAIN CONTENT – push down by AppBar height */}
      <Box
        component="main"
        sx={{
          flex: 1,
          pt: {
            xs: `${totalAppBarHeight.xs}px`,
            sm: `${totalAppBarHeight.sm}px`,
            md: `${totalAppBarHeight.md}px`,
          },
          display: "flex",
          flexDirection: "column",
          mx: "auto",
          width: "100%",
          maxWidth: "1920px",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* TWO-COLUMN LAYOUT */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            gap: { xs: 0, md: 2 },
          }}
        >
          {/* LEFT: Canvas + LineList */}
          <Box
            sx={{
              flex: "1 1 0",
              display: "flex",
              flexDirection: "column",
              width: { xs: "100%", md: "calc(100% - 350px)" }, // 350px = drawer width
              minWidth: 0,
            }}
          >
            <CanvasArea />
          </Box>
          <Box
            sx={{
              width: { xs: 0, sm: 0, md: 350 },
              flexShrink: 0,
              display: { xs: "none", sm: "none", md: "block" },
            }}
          ></Box>
        </Box>
      </Box>
    </Box>
  );
});

const App = () => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <ThemeProvider theme={theme}>
      <DataProvider>
        <PricingProvider>
          <ErrorBoundary>
            <MainContent />
          </ErrorBoundary>
        </PricingProvider>
      </DataProvider>
    </ThemeProvider>
  );
};

export default memo(App);
